import axios from 'axios';
import React, { Fragment, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft, FaSpinner, FaImage, FaEdit, FaSave } from "react-icons/fa";
import LoadingScreen from '../../components/LoadingScreen';

const API_BASE_URL = process.env.REACT_APP_API_URL;

function EditBrandPage() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [brandData, setBrandData] = useState({ name: '', image: '' });
    const [imageUploading, setImageUploading] = useState(false);
    const [savingBrand, setSavingBrand] = useState(false);
    const [loading, setLoading] = useState(true);
    const token = localStorage.getItem('authToken');

    const handleApiError = (error) => {
        console.error('API Error:', error);
        alert(error.response?.data?.message || 'Something went wrong. Please try again.');
    };

    const handleBrandData = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/single-brand-data/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setBrandData(response.data);
            console.log(response.data);
        } catch (error) {
            handleApiError(error);
            setTimeout(() => navigate('/admin/brands'), 2000);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (event) => {
        setBrandData({
            ...brandData,
            [event.target.name]: event.target.value,
        });
    };

    const handleImageUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            alert('Only JPG, PNG, or WebP images are allowed.');
            return;
        }

        if (file.size > 5 * 1024 * 1024) { // 5MB limit
            alert('File size must be under 5MB.');
            return;
        }

        try {
            setImageUploading(true);
            const formData = new FormData();
            formData.append('image', file);

            const response = await axios.post(`${API_BASE_URL}/upload-prod-img`, formData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setBrandData(prev => ({ ...prev, image: response.data.path }));
            alert('Image uploaded successfully!');
        } catch (error) {
            handleApiError(error);
        } finally {
            setImageUploading(false);
        }
    };

    const validateFields = () => {
        if (!brandData.name.trim()) {
            alert('Brand name is required');
            return false;
        }
        if (!brandData.image.trim()) {
            alert('Brand image is required');
            return false;
        }
        return true;
    };

    const handleUpdateBrand = async () => {
        if (!validateFields()) return;

        if (!brandData.image) {
            alert('Brand image is required');
            return;
        }

        try {
            setSavingBrand(true);
            const response = await axios.put(`${API_BASE_URL}/update-brand/${id}`, brandData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert(response.data.message);
            navigate('/admin/brands');
        } catch (error) {
            handleApiError(error);
        } finally {
            setSavingBrand(false);
        }
    };

    const handleBackButtonClick = () => {
        navigate('/admin/brands');
    };

    useEffect(() => {
        handleBrandData();
    }, [id]);

    if (loading) {
        return (
            <div className="container-fluid p-3 pt-2 g-0 adma">
                <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: 'calc(100vh - 120px)' }}>
                    <LoadingScreen />
                    <p className="mt-3 text-muted small fw-semibold" style={{ letterSpacing: '0.5px' }}>Loading brand details...</p>
                </div>
            </div>
        );
    }

    return (
        <Fragment>
            <div className="container-fluid p-3 pt-2 g-0 adma">
                <div className="row">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="section-title">
                                <h4 className="mt-1">EDIT BRAND</h4>
                                <p className="text-muted p-0 m-0">Update brand information and image</p>
                            </div>
                            <button 
                                type="button"
                                className="back-btn-pill"
                                onClick={handleBackButtonClick}
                                title="Back to Brands"
                            >
                                <FaArrowLeft />
                                Back to Brands
                            </button>
                        </div>
                    </div>
                </div>

                <div className="row justify-content-center mt-4">
                    <div className="col-lg-8 col-md-10 col-12">
                        <div className="details-card-custom edit-brand-card-custom">
                            <div className="details-card-header">
                                <h5>
                                    <FaEdit className="text-danger me-2" />
                                    Brand Information
                                </h5>
                            </div>
                            <div className="details-card-body p-4">
                                <div className="row align-items-center">
                                    <div className="col-md-6">
                                        <div className="mb-4">
                                            <label className="form-label form-label-custom">Brand Name</label>
                                            <input
                                                type="text"
                                                className="form-control form-control-custom"
                                                name="name"
                                                value={brandData.name || ''}
                                                onChange={handleChange}
                                                placeholder="Enter brand name"
                                            />
                                        </div>
                                        
                                        <div className="mb-4">
                                            <label className="form-label form-label-custom">Brand Image</label>
                                            <input
                                                type="file"
                                                className="form-control form-control-custom"
                                                name="image"
                                                onChange={handleImageUpload}
                                                accept="image/*"
                                            />
                                            {imageUploading && (
                                                <div className="mt-2">
                                                    <FaSpinner className="spinner-border spinner-border-sm me-2 text-danger" />
                                                    <small className="text-muted">Uploading image...</small>
                                                </div>
                                            )}
                                            {brandData.image && !imageUploading && (
                                                <div className="mt-2">
                                                    <small className="text-success fw-semibold">
                                                        <FaImage className="me-1" />
                                                        Image uploaded successfully
                                                    </small>
                                                </div>
                                            )}
                                        </div>

                                        <button
                                            className="save-brand-btn w-100"
                                            disabled={savingBrand || imageUploading}
                                            onClick={handleUpdateBrand}
                                        >
                                            {savingBrand ? (
                                                <>
                                                    <FaSpinner className="spinner-border spinner-border-sm me-2" />
                                                    Updating Brand...
                                                </>
                                            ) : (
                                                <>
                                                    <FaSave className="me-2" />
                                                    Update Brand
                                                </>
                                            )}
                                        </button>
                                    </div>
                                    
                                    <div className="col-md-6 mt-4 mt-md-0">
                                        <div className="text-center">
                                            <label className="form-label form-label-custom text-center d-block mb-3">Current Image Preview</label>
                                            <div className="brand-preview-container">
                                                {brandData.image ? (
                                                    <img
                                                        src={brandData.image.startsWith('http') ? brandData.image : `${API_BASE_URL}${brandData.image}`}
                                                        alt="Brand Preview"
                                                        className="brand-preview-image"
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                            e.target.parentNode.innerHTML = '<p class="text-muted mb-0">No image uploaded</p>';
                                                        }}
                                                    />
                                                ) : (
                                                    <div className="brand-preview-placeholder">
                                                        <p className="text-muted mb-0">No image uploaded</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style jsx>{`
                .edit-brand-card-custom {
                    border-left: 1px solid #cbd5e1 !important;
                    border-top: 1px solid #cbd5e1 !important;
                }

                .form-label-custom {
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
                    font-size: 0.72rem !important;
                    text-transform: uppercase !important;
                    font-weight: 700 !important;
                    color: #64748b !important;
                    letter-spacing: 0.5px !important;
                    margin-bottom: 6px !important;
                    display: block !important;
                }
                
                .form-control-custom {
                    border: 1px solid #cbd5e1 !important;
                    border-radius: 0 !important;
                    font-size: 0.88rem !important;
                    padding: 8px 12px !important;
                }
                
                .form-control-custom:focus {
                    border-color: #ed3237 !important;
                    box-shadow: 0 0 0 3px rgba(237, 50, 55, 0.1) !important;
                    outline: none !important;
                }
                
                .brand-preview-container {
                    border: 1px dashed #cbd5e1;
                    border-radius: 0;
                    padding: 15px;
                    min-height: 220px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #f8fafc;
                }
                
                .brand-preview-image {
                    max-width: 100%;
                    max-height: 180px;
                    object-fit: contain;
                    border-radius: 0;
                    border: 1px solid #cbd5e1;
                    padding: 4px;
                    background: #ffffff;
                }
                
                .brand-preview-placeholder {
                    text-align: center;
                    color: #94a3b8;
                }
            `}</style>
        </Fragment>
    );
}

export default EditBrandPage;
